/**
 * ============================================================================
 * RecordsDispositionTray — Phase 6a frontend (T-P6a-W3-03)
 * Owner: Claude 3 (Frontend & UX).
 * Route: /retention/disposition
 * Purpose: queue of disposition tasks awaiting QA Manager + Doc Controller
 *          dual signature. Filtering happens client-side via tabs so the
 *          counts stay coherent between switches without re-fetching.
 *
 * UX RULES:
 *   - Tabs: Pending / Awaiting Signatures / Ready / Blocked.
 *   - Each tab shows a count badge.
 *   - BLOCKED tasks render the blocked_reason + a "Why?" disclosure.
 *   - Sign actions enforce WebAuthn (SignDispositionDialog).
 *
 * URS rows: RET-008..014, SIG-006/011.
 * ============================================================================ */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card, CardBody, CardHeader, PageHero } from '@edms/design-system';
import wstyles from '../WebhookAdmin/WebhookAdmin.module.css';
import styles from './RecordsDispositionTray.module.css';
import {
  useDispositionTasksQuery,
  type DispositionTask,
  type DispositionTaskState,
  type SignerRole,
} from './useDispositionTasksQuery';
import { DispositionTaskRow } from './DispositionTaskRow';
import { SignDispositionDialog } from './SignDispositionDialog';

type TabKey = 'PENDING' | 'AWAITING_SIGNATURES' | 'READY' | 'BLOCKED';

const TABS: TabKey[] = ['PENDING', 'AWAITING_SIGNATURES', 'READY', 'BLOCKED'];

const TAB_LABEL_KEY: Record<TabKey, string> = {
  PENDING: 'retention.disposition.tabs.pending',
  AWAITING_SIGNATURES: 'retention.disposition.tabs.awaiting',
  READY: 'retention.disposition.tabs.ready',
  BLOCKED: 'retention.disposition.tabs.blocked',
};

const TAB_LABEL_DEFAULT: Record<TabKey, string> = {
  PENDING: 'Pending',
  AWAITING_SIGNATURES: 'Awaiting Signatures',
  READY: 'Ready',
  BLOCKED: 'Blocked',
};

function matchesTab(state: DispositionTaskState, tab: TabKey): boolean {
  // PENDING and AWAITING_SIGNATURES are distinct server states; READY and
  // BLOCKED map one-to-one. COMPLETED tasks never surface in the tray.
  return state === tab;
}

export function RecordsDispositionTray() {
  const { t, i18n } = useTranslation();
  const tasksQ = useDispositionTasksQuery();
  const nf = useMemo(() => new Intl.NumberFormat(i18n.language), [i18n.language]);
  const [tab, setTab] = useState<TabKey>('PENDING');
  const [signTarget, setSignTarget] = useState<{
    task: DispositionTask;
    role: SignerRole;
  } | null>(null);

  const allTasks: DispositionTask[] = tasksQ.data?.items ?? [];

  const tabCounts: Record<TabKey, number> = useMemo(() => {
    const counts: Record<TabKey, number> = {
      PENDING: 0,
      AWAITING_SIGNATURES: 0,
      READY: 0,
      BLOCKED: 0,
    };
    for (const task of allTasks) {
      if (task.state in counts) {
        counts[task.state as TabKey] += 1;
      }
    }
    return counts;
  }, [allTasks]);

  const visible = useMemo(
    () => allTasks.filter((task) => matchesTab(task.state, tab)),
    [allTasks, tab],
  );

  return (
    <div className={wstyles.adminScreen}>
      <PageHero
        eyebrow={t('retention.disposition.eyebrow', { defaultValue: 'Quality' })}
        title={t('retention.disposition.title', {
          defaultValue: 'Records Disposition Tray',
        })}
        subtitle={t('retention.disposition.subtitle', {
          defaultValue:
            'Records whose retention has ended, awaiting QA Manager and Doc Controller signatures before disposition.',
        })}
      />

      <Card>
        <CardHeader>
          <ul className={styles.tabs} role="tablist" aria-label={t('retention.disposition.tabsAria', { defaultValue: 'Disposition state' })}>
            {TABS.map((k) => {
              const active = tab === k;
              return (
                <li key={k} role="presentation">
                  <button
                    type="button"
                    role="tab"
                    aria-selected={active}
                    aria-controls={`tab-panel-${k}`}
                    id={`tab-${k}`}
                    tabIndex={active ? 0 : -1}
                    className={`${styles.tabButton} ${active ? styles.tabButtonActive : ''}`}
                    onClick={() => setTab(k)}
                  >
                    <span>
                      {t(TAB_LABEL_KEY[k], { defaultValue: TAB_LABEL_DEFAULT[k] })}
                    </span>
                    <span className={styles.tabBadge} aria-hidden="true">
                      {nf.format(tabCounts[k])}
                    </span>
                    <span className="sr-only">
                      {t('retention.disposition.tabsCountAria', {
                        defaultValue: '({{count}} tasks)',
                        count: tabCounts[k],
                      })}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </CardHeader>
        <CardBody>
          <div
            role="tabpanel"
            id={`tab-panel-${tab}`}
            aria-labelledby={`tab-${tab}`}
          >
            {tasksQ.isLoading && (
              <div role="status" aria-live="polite" aria-label={t('common.loading', { defaultValue: 'Loading…' })}>
                {[0, 1, 2].map((i) => (
                  <div key={i} className={styles.skeletonCard} aria-hidden="true" />
                ))}
              </div>
            )}

            {tasksQ.isError && (
              <div className={styles.errorBanner} role="alert">
                <span>
                  {t('retention.disposition.errors.loadFailed', {
                    defaultValue: 'Could not load disposition tasks.',
                  })}
                </span>
                <Button variant="secondary" size="sm" onClick={() => tasksQ.refetch()}>
                  {t('retention.disposition.actions.retry', { defaultValue: 'Retry' })}
                </Button>
              </div>
            )}

            {!tasksQ.isLoading && !tasksQ.isError && visible.length === 0 && (
              <p className={styles.emptyState}>
                {t('retention.disposition.empty', {
                  defaultValue: 'No records pending disposition.',
                })}
              </p>
            )}

            {visible.length > 0 && (
              <ul className={styles.cardList}>
                {visible.map((task) => (
                  <li key={task.task_id} className={styles.cardListItem}>
                    <DispositionTaskRow
                      task={task}
                      onSign={(role) => setSignTarget({ task, role })}
                    />
                  </li>
                ))}
              </ul>
            )}
          </div>
        </CardBody>
      </Card>

      {signTarget && (
        <SignDispositionDialog
          task={signTarget.task}
          role={signTarget.role}
          onClose={() => setSignTarget(null)}
          onSigned={() => {
            setSignTarget(null);
            tasksQ.refetch();
          }}
        />
      )}
    </div>
  );
}
